import { Button, LoadingOverlay, Stack, Text } from '@mantine/core'
import { IconArrowLeft, IconHexagons, IconPaperclip, IconStar } from '@tabler/icons-react'
import type { DefaultError, UseQueryResult } from '@tanstack/react-query'
import { useQueries, useQuery } from '@tanstack/react-query'
import { observer } from 'mobx-react-lite'
import type { RefObject } from 'react'
import { createContext, useContext, useEffect, useMemo, useRef } from 'react'
import { useParams } from 'wouter'

import { CollapsibleContentSection } from '@/components/CollapsibleContentSection'
import { EmptyState } from '@/components/EmptyState'
import { Screen } from '@/components/Screen'
import type { RouteParams } from '@/configs/routes'
import { useAllEsks } from '@/hooks/useAllEsks'
import { useEffectiveMediaMode } from '@/hooks/useEffectiveMediaMode'
import { useGlobalState } from '@/hooks/useGlobalState'
import type {
  GetContentModuleResponse,
  GetContentResponse,
  GetSidekicksResponse,
  GetStepsResponse,
} from '@/services/api/api.types'
import { splitMediaSteps } from '@/utils/contentModuleForm'
import { isHelpModule } from '@/utils/contentModuleTables'
import { navigateTo } from '@/utils/navigation'

import { NotFoundScreen } from '../404Screen'
import { ContentDataModal } from './ContentDataModal'
import { CustomizeDeliverySection } from './CustomizeDeliverySection'
import { ModuleSection } from './ModuleSection'
import { StepSection } from './StepSection'

export type ModuleContentSection = {
  taskId?: string
  modal: RefObject<ContentDataModal | null>
  isMediaMode: boolean

  cmQuery: UseQueryResult<GetContentModuleResponse, DefaultError>
  cmContentQuery: UseQueryResult<GetContentResponse, DefaultError>
  stepsQuery: UseQueryResult<GetStepsResponse, DefaultError>
  sidekicksQuery: UseQueryResult<GetSidekicksResponse, DefaultError>
  sidekickPoolQuery: UseQueryResult<GetSidekicksResponse | null, DefaultError>
}

const ModuleContentSectionContext = createContext<ModuleContentSection>({} as ModuleContentSection)
export const useContentModuleScreenData = () => useContext(ModuleContentSectionContext)

export const ContentModuleScreen = observer(function ContentModuleScreen() {
  const params = useParams<RouteParams['contentModule']>()

  const modal = useRef<ContentDataModal>(null)

  const { api, uiStore } = useGlobalState()

  const taskId = params.id

  const cmQuery = useQuery({
    queryKey: ['cm', taskId],
    queryFn: () => api.getContentModule({ task_id: taskId! }),
  })

  const cmContentQuery = useQuery({
    queryKey: ['cm', 'content', taskId],
    queryFn: () => api.getContent({ task_id: taskId! }),
    enabled: cmQuery.isSuccess,
  })

  const stepsQuery = useQuery({
    queryKey: ['steps', taskId],
    queryFn: () => api.getSteps({ task_id: taskId! }),
    enabled: cmQuery.isSuccess,
  })

  const { sidekicksQuery, sidekickPoolQuery } = useAllEsks()
  const { isMediaMode } = useEffectiveMediaMode({
    deliveryStyle: cmQuery.data?.presentationStyle,
    isMediaMode: cmQuery.data?.isMediaMode,
  })

  const isSwiMskCm = cmQuery.data ? isHelpModule(cmQuery.data) : false

  const isVisibleToMsk = useMemo(() => !!cmQuery.data?.visibleToMsk, [cmQuery.data])

  // Fetch content keys for each step when it's an MSK content module
  const stepContentQueries = useQueries({
    queries: (stepsQuery.data ?? []).map(step => ({
      queryKey: ['step', 'content', step.stepId],
      queryFn: () => api.getContent({ step_id: step.stepId }),
      enabled: !!step.stepId && isSwiMskCm,
    })),
  })

  useEffect(() => {
    if (cmQuery.isSuccess || cmQuery.isError) {
      uiStore.hideLoadingOverlay()
    } else {
      uiStore.showLoadingOverlay()
    }
  }, [cmQuery.isSuccess, cmQuery.isError])

  const common = { taskId, modal, isMediaMode }

  const operations = {
    cmQuery,
    cmContentQuery,
    stepsQuery,
    sidekicksQuery,
    sidekickPoolQuery,
  }

  if (cmQuery.isError) {
    return <NotFoundScreen />
  }

  // Important to wait on all queries - we need to know if this is a sponsor MSK CM before fetching the file content
  if (cmQuery.isLoading || cmContentQuery.isLoading || stepsQuery.isLoading) {
    return <LoadingOverlay visible />
  }

  return (
    <Screen
      title={['Previewing Module', cmQuery.data?.title].filter(Boolean).join(': ')}
      icon={IconHexagons}
      description="Use this page to preview Content Modules created by other SMEs. You will only be able to modify the content if you created it."
      BodyStackProps={{ gap: 'xxl' }}>
      <ModuleContentSectionContext.Provider value={{ ...common, ...operations }}>
        <ModuleSection />
        {!isVisibleToMsk && !isMediaMode && <CustomizeDeliverySection />}
        {isMediaMode ? (
          <MediaModeStepsView
            steps={stepsQuery.data ?? []}
            stepContentQueries={stepContentQueries}
            isSwiMskCm={isSwiMskCm}
          />
        ) : (
          stepsQuery.data?.map((s, i) => (
            <StepSection
              key={s.stepId}
              index={i}
              step={s}
              fileId={isSwiMskCm ? (stepContentQueries[i]?.data?.contentKey ?? undefined) : undefined}
            />
          ))
        )}
        {/* <ModuleFAQSection /> */}
        <ContentDataModal ref={modal} />
        <CollapsibleContentSection
          id="actions"
          ContentElement={
            <Button onClick={() => navigateTo('contentModules')} leftSection={<IconArrowLeft />}>
              Back to Modules
            </Button>
          }
        />
      </ModuleContentSectionContext.Provider>
    </Screen>
  )
})

type MediaModeStepsViewProps = {
  steps: GetStepsResponse
  // Loosened from useQueries' generic — only `contentKey` is consumed here.
  stepContentQueries: { data?: { contentKey?: string | null } | undefined }[]
  isSwiMskCm: boolean
}

function MediaModeStepsView({ steps, stepContentQueries, isSwiMskCm }: MediaModeStepsViewProps) {
  const { primary, supplementary } = splitMediaSteps(steps)

  return (
    <>
      <Stack gap={0}>
        <Text size="xl" fw={600}>
          Primary Media File
        </Text>
        <Text size="md" c="dimmed">
          The main media file for this module.
        </Text>
      </Stack>
      {primary ? (
        <StepSection
          key={primary.stepId}
          index={0}
          step={primary}
          fileId={isSwiMskCm ? (stepContentQueries[0]?.data?.contentKey ?? undefined) : undefined}
        />
      ) : (
        <EmptyState size="sm" Icon={IconStar} title="No primary media file uploaded" />
      )}

      <Stack gap={0}>
        <Text size="xl" fw={600}>
          Supplementary Content
        </Text>
        <Text size="md" c="dimmed">
          Additional files that enrich this module.
        </Text>
      </Stack>
      {supplementary.length > 0 ? (
        supplementary.map((s, i) => (
          <StepSection
            key={s.stepId}
            index={i + 1}
            step={s}
            fileId={isSwiMskCm ? (stepContentQueries[i + 1]?.data?.contentKey ?? undefined) : undefined}
          />
        ))
      ) : (
        <EmptyState size="sm" Icon={IconPaperclip} title="No supplementary content added" />
      )}
    </>
  )
}
